import { Injectable } from '@angular/core';
import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { BaseHttpService } from 'src/app/auth/base-capacitor-http';
import { HttpClient } from '@angular/common/http';
import { Yard, YardUpdateOption } from '../app/yard.service';
import { Move, MoveUpdateOption } from '../app/moves.service';
import { Trailer, TrailerUpdateOption } from '../app/trailer.service';
import { EncryptedData } from 'src/app/auth/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';

//AI Stuffs
export class AIPrompt {
  prompt: string;
  messages: Message[];
}

export class AIResponse {
  id: string
  object: string
  created: number
  model: string
  choices: Choice[]
}

export class Choice {
  index: number
  message: Message
}

export class Message {
  role: string
  content: string
}

export class ChatMessage {
  chatId: number;
  senderId: string;
  sender: string;
  text: string;
  status: string;
  timestamp: Date;  
}

export class Chats{
  senderId: string;
  sender: string;
  messages: ChatMessage[];
}

export class DirectMessage {
  senderId: string;
  sender: string;
  recipientId: string;
  recipient: string;
  text: string;
  status?: string;
  timestamp: Date;  
}

export class DMS{
  senderId: string;
  sender: string;
  messages: DirectMessage[];
}

export class User{
  id: string;
  name: string;
  status: string;
  image: string;
}

export class UpdateMessage{
  uid: number;
  senderId: string;
  instance: number;
  type: string;
  update: string;
  data: string;
}

export enum UpdateMessageType{
  TRAILER = 'TRAILER',
  YARD = 'YARD',
  MOVE = 'MOVE',
  MESSAGE = 'MESSAGE',
  MESSAGE_HISTORY = 'MESSAGE_HISTORY'
}

@Injectable({
  providedIn: 'root'
})
export class WebsocketService extends BaseHttpService{
  private baseUrl: string = environment.server.serverURL;
  private endPointUrl: string;

  private socket: WebSocket | null = null;
  private chatMessages: BehaviorSubject<ChatMessage> = new BehaviorSubject(null);
  private updateMessages: BehaviorSubject<UpdateMessage> = new BehaviorSubject(null);
  private directMessages: BehaviorSubject<DirectMessage> = new BehaviorSubject(null);
  private isConnected:BehaviorSubject<boolean> = new BehaviorSubject(false);
  private chatSubscription: StompSubscription = null;
  private dmSubscription: StompSubscription = null;
  private updateSubscription: StompSubscription = null;


  private stompClient: Client | null = null;
  constructor(client: HttpClient, snackbar: MatSnackBar) {
    super(client, snackbar);
  }

  async getAIChatBotResponse(prompt: AIPrompt): Promise<AIResponse>{
    this.endPointUrl = this.baseUrl + 'ai/chat-bot';
    let body = {
      payload: this.encrypt<AIPrompt>(prompt, this.KEY)
    }
    const encryptedData = await this.post<EncryptedData>(this.endPointUrl, body);
    return this.decryptResponse<AIResponse>(encryptedData.payload, this.KEY, encryptedData.iv);
  }
  
  getAiMessages(messages: ChatMessage[]): Message[]{
    return messages.map(message =>{
      return {
        role: message.sender,
        content: message.text
      }
    });
  }

  checkConnection(): Observable<boolean>{
    return this.isConnected.asObservable();
  }

  getChatMessages(): Observable<ChatMessage>{
    return this.chatMessages.asObservable();
  }

  getDirectMessages(): Observable<DirectMessage>{
    return this.directMessages.asObservable();
  }

  getUpdateMessages(): Observable<UpdateMessage>{
    return this.updateMessages.asObservable();
  }

  async reconnect(){
    await this.disconnect();
    await this.connect();
  }

  async connect(){
    if(this.stompClient && this.stompClient.connected){
      
      return;
    }
    const authHeaders = await this.getAuthHeaders();
    this.stompClient = new Client({
      brokerURL: environment.server.socketUrl + 'chat',
      connectHeaders: authHeaders,
      reconnectDelay: 5000,
      onConnect: () => {
        this.isConnected.next(true);
      },
      onDisconnect:() => {
        this.isConnected.next(false);
      },
      onStompError: (frame) =>{
        console.error('Broker error', frame.headers['message'], frame.body);
        this.isConnected.next(false);
      },
      onWebSocketError: (error) => {
        console.error('websocket error: ', error);
        this.isConnected.next(false);
      }
      
    });
    this.stompClient.activate();
  }

 
     /**
   * Function to listen to chat messages
   * When a message is received, it is added to the messages array
   * The message is then scrolled into view
   * the message is also marked as incoming which is used to style the message
   * the subscription is unsubscribed when called again
   */
  listenToUpdates(instance: number, userChatId: string): Promise<void> {
    if(!this.stompClient || !this.stompClient.connected){
      return;
    }
    if(this.chatSubscription){
      this.chatSubscription.unsubscribe();
    }

    this.updateSubscription = this.stompClient.subscribe('/instance/' + instance, (message: IMessage) =>{
        let chatMessage: UpdateMessage = JSON.parse(message.body);
        this.updateMessages.next(chatMessage);        
      });
  }


   /**
   * Function to listen to chat messages
   * When a message is received, it is added to the messages array
   * The message is then scrolled into view
   * the message is also marked as incoming which is used to style the message
   * the subscription is unsubscribed when called again
   */
  listenToChannel(channel: string): Promise<void> {
    if(!this.stompClient || !this.stompClient.connected){
      return;
    }
    if(this.chatSubscription){
      this.chatSubscription.unsubscribe();
    }
    

    this.chatSubscription = this.stompClient.subscribe('/topic/' + channel, (message: IMessage) =>{
        let chatMessage: ChatMessage = JSON.parse(message.body);
        this.chatMessages.next(chatMessage);
      });
  }

  prepTrailerUpdateMessage(instance: number, uid: number, chatId: string, trailer: Trailer, update: TrailerUpdateOption): UpdateMessage{
    const updateMessage: UpdateMessage = {
      instance: instance,
      senderId: chatId,
      type: UpdateMessageType.TRAILER,
      update: update,
      data: JSON.stringify(trailer),
      uid: uid
    };

    return updateMessage;
  }

  prepYardUpdateMessage(instance: number, uid: number, chatId: string, yard: Yard, update: YardUpdateOption): UpdateMessage{
     const updateMessage: UpdateMessage = {
      instance: instance,
      senderId: chatId,
      type: UpdateMessageType.YARD,
      update: update,
      data: JSON.stringify(yard),
      uid: uid
    };

    return updateMessage;
  }

  perpMoveUpdateMessage(instance: number, uid: number, chatId: string, move: Move, redirect: Yard, update: MoveUpdateOption): UpdateMessage{
     let body = {
      move: move,
      redirect: redirect
    }
    const updateMessage: UpdateMessage = {
      instance: instance,
      senderId: chatId,
      type: UpdateMessageType.MOVE,
      update: update,
      data: JSON.stringify(body),
      uid: uid
    };

    return updateMessage;
  }

  prepConvoHistory(instance: number, uid: number, chatId: string, messages: ChatMessage[]){
    const updateMessage: UpdateMessage = {
      instance: instance,
      senderId: chatId,
      type: UpdateMessageType.MESSAGE_HISTORY,
      update: null,
      data: JSON.stringify(messages),
      uid: uid
    }
    return updateMessage;
  }

  prepMessage(chatId: number, uid: number, senderId: string, sender: string, text: string): UpdateMessage {
    const encryptedText = this.encrypt<string>(text, this.KEY);
    const message: ChatMessage = {
      chatId: chatId,
      senderId: senderId,
      sender: sender,
      text: encryptedText,
      status: 'outgoing',
      timestamp: new Date()
    }
    const updateMessage: UpdateMessage = {
      instance: chatId,
      senderId: senderId,
      type: UpdateMessageType.MESSAGE,
      update: null,
      data: JSON.stringify(message),
      uid: uid
    }
    return updateMessage;
  }

  getDecryptedMessage(update: UpdateMessage){
    const message: ChatMessage = JSON.parse(update.data);
    message.text = this.decrypt<string>(message.text, this.KEY);
    return message;
  }


  async sendUpdate(update: UpdateMessage){
    const authHeaders = await this.getAuthHeaders();
     if (this.stompClient && this.stompClient.connected) {
        this.stompClient.publish({
          destination: '/app/yard-hostler',
          headers: authHeaders,
          body: JSON.stringify(update)
        });
     }
  }

  // Function to send messages to the public group chat
  async sendMessage(message: ChatMessage) {
    const authHeaders = await this.getAuthHeaders();
    if (this.stompClient && this.stompClient.connected) {
      this.stompClient.publish({
        destination: `/app/send`,  // Maps to @MessageMapping("/send")
        headers: authHeaders,
        body: JSON.stringify(message)
      });
      
    }
  }
    /**
   * Function to listen for direct messages
   * When a direct message is received, it is added to the messages array
   * The message is then scrolled into view
   * the subscription is unsubscribed when called again
   * 
   */
  listenForDm(): void{

    if(!this.stompClient || !this.stompClient.connected){
      return;
    }
    if(this.dmSubscription){
      this.dmSubscription.unsubscribe();
    }

    this.dmSubscription = this.stompClient.subscribe('/user/dm/messages', (message: IMessage) =>{
        let dm: DirectMessage = JSON.parse(message.body);
        this.directMessages.next(dm);
      });
  }

   // Function to send a direct message to a specific user
  async sendPrivateMessage(senderId: string, sender: string, recipientId: string, recipient: string, text: string) {
    const authHeaders = await this.getAuthHeaders();
    if (this.stompClient && this.stompClient.connected) {
      this.stompClient.publish({
        destination: `/app/dm`,  // Maps to @MessageMapping("/dm")
        headers: authHeaders,
        body: JSON.stringify({senderId, sender, recipientId, recipient, text, status: 'outgoing', timestamp: new Date() })
      });
    }
  }


  generateId(): string{
    return Math.random().toString(36).substr(2, 9);
  }

  switchInstance(){
     this.isConnected.next(false);
      this.chatMessages.next(null);
      this.updateMessages.next(null);
      this.directMessages.next(null);
      if(this.chatSubscription){
        this.chatSubscription.unsubscribe();
      }
      if(this.dmSubscription){
        this.dmSubscription.unsubscribe();
      }
      if(this.updateSubscription){
        this.updateSubscription.unsubscribe();
      }
  }

  disconnect() {
    if (this.stompClient) {  
      this.switchInstance();
      this.stompClient.deactivate();
    }
  }
}
